/**
 * Data transformation utilities for Technology Stack visualizations
 *
 * Converts technology database into visualization-ready formats
 */

import { TechnologyOption } from './tech-stack-types';

// ==========================================
// TYPE DEFINITIONS
// ==========================================

export interface RadarQuadrant {
  name: string;
  angle: number;
  technologies: RadarTechnology[];
}

export interface RadarTechnology {
  id: string;
  name: string;
  ring: 'adopt' | 'trial' | 'assess' | 'hold';
  quadrant: string;
  x: number;
  y: number;
  category: string;
  description: string;
  maturity: string;
  popularityTrend: string;
}

export interface TrendPoint {
  date: string;
  value: number;
  metric: 'github-stars' | 'npm-downloads' | 'stackoverflow-questions';
}

export interface TrendData {
  technologyId: string;
  technologyName: string;
  data: TrendPoint[];
}

export interface MarketShareData {
  category: string;
  technologies: {
    name: string;
    value: number;
    percentage: number;
    color: string;
  }[];
}

export interface ScatterPoint {
  id: string;
  name: string;
  maturity: number;
  communitySize: number;
  popularityTrend: number;
  category: string;
  color: string;
}

export interface HeatmapData {
  technologies: {
    id: string;
    name: string;
    category: string;
    scores: {
      compliance: number;
      technical: number;
      operational: number;
      cost: number;
      ecosystem: number;
      maturity: number;
    };
  }[];
}

// ==========================================
// TECHNOLOGY RADAR DATA
// ==========================================

const RADAR_QUADRANTS = [
  { name: 'Languages & Frameworks', categories: ['frontend-framework', 'backend-framework', 'mobile-framework'] },
  { name: 'Infrastructure & Operations', categories: ['cloud-provider', 'ci-cd', 'container-orchestration', 'cdn'] },
  { name: 'Data & Storage', categories: ['database', 'cache', 'search', 'message-queue'] },
  { name: 'Tools & Platforms', categories: ['monitoring', 'logging', 'observability', 'analytics', 'testing', 'authentication', 'api-gateway', 'secret-management', 'email-service'] },
];

/**
 * Determine radar ring based on maturity and popularity
 */
function determineRing(tech: TechnologyOption): 'adopt' | 'trial' | 'assess' | 'hold' {
  const { maturity } = tech.technical;
  const { popularityTrend } = tech.ecosystem;

  // HOLD - Legacy or declining
  if (maturity === 'legacy' || popularityTrend === 'declining') {
    return 'hold';
  }

  // ADOPT - Mature and stable/growing
  if (maturity === 'mature' && (popularityTrend === 'stable' || popularityTrend === 'growing')) {
    return 'adopt';
  }

  // TRIAL - Stable with growing popularity
  if (maturity === 'stable' && (popularityTrend === 'growing' || popularityTrend === 'rapidly-growing')) {
    return 'trial';
  }

  // ASSESS - Early stage with rapid growth
  if ((maturity === 'early' || maturity === 'experimental') && popularityTrend === 'rapidly-growing') {
    return 'assess';
  }

  // TRIAL - Default for stable technologies
  if (maturity === 'stable') {
    return 'trial';
  }

  // ASSESS - Default for early stage
  return 'assess';
}

/**
 * Get quadrant for a technology based on its category
 */
function getQuadrant(category: string): string {
  for (const quadrant of RADAR_QUADRANTS) {
    if (quadrant.categories.includes(category)) {
      return quadrant.name;
    }
  }
  return 'Tools & Platforms'; // Default
}

/**
 * Convert polar coordinates to cartesian
 */
function polarToCartesian(angle: number, radius: number): { x: number; y: number } {
  const radians = (angle - 90) * (Math.PI / 180);
  return {
    x: radius * Math.cos(radians),
    y: radius * Math.sin(radians),
  };
}

/**
 * Prepare data for Technology Radar visualization
 */
export function prepareRadarData(technologies: TechnologyOption[]): RadarQuadrant[] {
  const quadrants = RADAR_QUADRANTS.map((q, index) => ({
    name: q.name,
    angle: index * 90,
    technologies: [] as RadarTechnology[],
  }));

  // Ring radii (0-1 normalized)
  const ringRadii = {
    adopt: 0.25,
    trial: 0.5,
    assess: 0.75,
    hold: 1.0,
  };

  technologies.forEach(tech => {
    const quadrantName = getQuadrant(tech.category);
    const quadrant = quadrants.find(q => q.name === quadrantName);
    if (!quadrant) return;

    const ring = determineRing(tech);
    const baseRadius = ringRadii[ring];

    // Add some randomization to avoid overlaps
    const radiusVariation = (Math.random() - 0.5) * 0.15;
    const radius = baseRadius + radiusVariation;

    // Spread technologies within quadrant (90 degrees)
    const quadrantIndex = quadrants.indexOf(quadrant);
    const angleMin = quadrantIndex * 90 + 10; // 10 degree margin
    const angleMax = quadrantIndex * 90 + 80; // 10 degree margin
    const angle = angleMin + Math.random() * (angleMax - angleMin);

    const position = polarToCartesian(angle, radius);

    quadrant.technologies.push({
      id: tech.id,
      name: tech.name,
      ring,
      quadrant: quadrantName,
      x: position.x,
      y: position.y,
      category: tech.category,
      description: tech.description,
      maturity: tech.technical.maturity,
      popularityTrend: tech.ecosystem.popularityTrend,
    });
  });

  return quadrants;
}

// ==========================================
// MARKET SHARE DATA
// ==========================================

const CHART_COLORS = [
  '#E89841', // Orange
  '#4A90E2', // Blue
  '#50C878', // Green
  '#9B59B6', // Purple
  '#E74C3C', // Red
  '#F39C12', // Yellow
  '#1ABC9C', // Teal
  '#34495E', // Dark Blue-Grey
  '#E67E22', // Darker Orange
  '#3498DB', // Lighter Blue
];

/**
 * Calculate market share based on community size and popularity
 */
function calculateTechnologyWeight(tech: TechnologyOption): number {
  // Base weight from community size
  const communityWeights = {
    'small': 1,
    'medium': 2,
    'large': 3,
    'very-large': 4,
  };

  // Multiplier from popularity trend
  const popularityMultipliers = {
    'declining': 0.7,
    'stable': 1.0,
    'growing': 1.3,
    'rapidly-growing': 1.6,
  };

  const baseWeight = communityWeights[tech.ecosystem.communitySize];
  const multiplier = popularityMultipliers[tech.ecosystem.popularityTrend];

  return baseWeight * multiplier;
}

/**
 * Prepare market share data by category
 */
export function prepareMarketShareData(technologies: TechnologyOption[]): MarketShareData[] {
  const categories = [...new Set(technologies.map(t => t.category))];

  return categories.map(category => {
    const categoryTechs = technologies.filter(t => t.category === category);
    const weights = categoryTechs.map(calculateTechnologyWeight);
    const total = weights.reduce((sum, w) => sum + w, 0);

    return {
      category,
      technologies: categoryTechs.map((tech, index) => ({
        name: tech.name,
        value: weights[index],
        percentage: (weights[index] / total) * 100,
        color: CHART_COLORS[index % CHART_COLORS.length],
      })).sort((a, b) => b.value - a.value),
    };
  });
}

/**
 * Prepare market share by license type
 */
export function prepareMarketShareByLicense(technologies: TechnologyOption[]): MarketShareData {
  const licenseTypes = [...new Set(technologies.map(t => t.license))];
  const weights = licenseTypes.map(license => {
    const techs = technologies.filter(t => t.license === license);
    return techs.reduce((sum, tech) => sum + calculateTechnologyWeight(tech), 0);
  });
  const total = weights.reduce((sum, w) => sum + w, 0);

  return {
    category: 'License Type',
    technologies: licenseTypes.map((license, index) => ({
      name: license.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      value: weights[index],
      percentage: (weights[index] / total) * 100,
      color: CHART_COLORS[index % CHART_COLORS.length],
    })).sort((a, b) => b.value - a.value),
  };
}

/**
 * Prepare market share by maturity level
 */
export function prepareMarketShareByMaturity(technologies: TechnologyOption[]): MarketShareData {
  const maturityLevels = [...new Set(technologies.map(t => t.technical.maturity))];
  const weights = maturityLevels.map(maturity => {
    const techs = technologies.filter(t => t.technical.maturity === maturity);
    return techs.reduce((sum, tech) => sum + calculateTechnologyWeight(tech), 0);
  });
  const total = weights.reduce((sum, w) => sum + w, 0);

  return {
    category: 'Maturity Level',
    technologies: maturityLevels.map((maturity, index) => ({
      name: maturity.charAt(0).toUpperCase() + maturity.slice(1),
      value: weights[index],
      percentage: (weights[index] / total) * 100,
      color: CHART_COLORS[index % CHART_COLORS.length],
    })).sort((a, b) => b.value - a.value),
  };
}

// ==========================================
// SCATTER PLOT DATA
// ==========================================

/**
 * Convert maturity to numeric value (0-100)
 */
function maturityToNumber(maturity: string): number {
  const map = {
    'experimental': 10,
    'early': 30,
    'stable': 60,
    'mature': 90,
    'legacy': 70, // Still mature but declining
  };
  return map[maturity as keyof typeof map] || 50;
}

/**
 * Convert community size to numeric value (0-100)
 */
function communitySizeToNumber(size: string): number {
  const map = {
    'small': 25,
    'medium': 50,
    'large': 75,
    'very-large': 100,
  };
  return map[size as keyof typeof map] || 50;
}

/**
 * Convert popularity trend to numeric value (multiplier for bubble size)
 */
function popularityTrendToNumber(trend: string): number {
  const map = {
    'declining': 0.5,
    'stable': 1.0,
    'growing': 1.5,
    'rapidly-growing': 2.0,
  };
  return map[trend as keyof typeof map] || 1.0;
}

/**
 * Prepare scatter plot data (Maturity vs Community Size)
 */
export function prepareScatterData(technologies: TechnologyOption[]): ScatterPoint[] {
  const categoryColors = new Map<string, string>();
  const categories = [...new Set(technologies.map(t => t.category))];
  categories.forEach((cat, index) => {
    categoryColors.set(cat, CHART_COLORS[index % CHART_COLORS.length]);
  });

  return technologies.map(tech => ({
    id: tech.id,
    name: tech.name,
    maturity: maturityToNumber(tech.technical.maturity),
    communitySize: communitySizeToNumber(tech.ecosystem.communitySize),
    popularityTrend: popularityTrendToNumber(tech.ecosystem.popularityTrend),
    category: tech.category,
    color: categoryColors.get(tech.category) || CHART_COLORS[0],
  }));
}

// ==========================================
// TREND DATA (MOCK)
// ==========================================

/**
 * Generate mock trend data based on popularity trend
 * This is a placeholder for future real API integration
 */
export function generateMockTrendData(
  tech: TechnologyOption,
  months: number = 12,
  metric: 'github-stars' | 'npm-downloads' | 'stackoverflow-questions' = 'github-stars'
): TrendData {
  const baseValues = {
    'github-stars': {
      'small': 1000,
      'medium': 10000,
      'large': 50000,
      'very-large': 200000,
    },
    'npm-downloads': {
      'small': 10000,
      'medium': 100000,
      'large': 1000000,
      'very-large': 10000000,
    },
    'stackoverflow-questions': {
      'small': 100,
      'medium': 1000,
      'large': 10000,
      'very-large': 50000,
    },
  };

  const growthRates = {
    'declining': 0.95,
    'stable': 1.0,
    'growing': 1.05,
    'rapidly-growing': 1.15,
  };

  const baseValue = baseValues[metric][tech.ecosystem.communitySize];
  const growthRate = growthRates[tech.ecosystem.popularityTrend];

  const data: TrendPoint[] = [];
  const today = new Date();

  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const monthsFromStart = months - i - 1;
    const value = Math.floor(baseValue * Math.pow(growthRate, monthsFromStart) * (0.9 + Math.random() * 0.2));

    data.push({
      date: date.toISOString().split('T')[0],
      value,
      metric,
    });
  }

  return {
    technologyId: tech.id,
    technologyName: tech.name,
    data,
  };
}

/**
 * Generate trend data for multiple technologies
 */
export function generateMultipleTrendData(
  technologies: TechnologyOption[],
  months: number = 12,
  metric: 'github-stars' | 'npm-downloads' | 'stackoverflow-questions' = 'github-stars'
): TrendData[] {
  return technologies.map(tech => generateMockTrendData(tech, months, metric));
}

// ==========================================
// HEATMAP DATA
// ==========================================

/**
 * Calculate normalized scores for heatmap
 */
function calculateHeatmapScores(tech: TechnologyOption): {
  compliance: number;
  technical: number;
  operational: number;
  cost: number;
  ecosystem: number;
  maturity: number;
} {
  // Compliance score (0-100)
  const complianceCount = [
    tech.compliance.gdpr,
    tech.compliance.hipaa,
    tech.compliance.soc2,
    tech.compliance.pci,
  ].filter(Boolean).length;
  const compliance = (complianceCount / 4) * 100;

  // Technical score (0-100)
  const perfScore = { 'low': 25, 'medium': 50, 'high': 75, 'very-high': 100 }[tech.technical.performance];
  const scaleScore = { 'low': 25, 'medium': 50, 'high': 75, 'very-high': 100 }[tech.technical.scalability];
  const learningScore = { 'very-steep': 25, 'steep': 50, 'moderate': 75, 'easy': 100 }[tech.technical.learningCurve];
  const technical = (perfScore + scaleScore + learningScore) / 3;

  // Operational score (0-100)
  const maintenanceScore = { 'high': 33, 'medium': 66, 'low': 100 }[tech.operations.maintenanceComplexity];
  const monitoringScore = { 'high': 33, 'medium': 66, 'low': 100 }[tech.operations.monitoringComplexity];
  const backupScore = { 'complex': 33, 'moderate': 66, 'simple': 100 }[tech.operations.backupRecovery];
  const operational = (maintenanceScore + monitoringScore + backupScore) / 3;

  // Cost score (0-100) - higher is better (lower cost)
  const costScore = tech.pricing.freeOption ? 100 :
                   tech.pricing.model === 'freemium' ? 80 :
                   tech.pricing.model === 'usage-based' ? 60 :
                   tech.pricing.model === 'per-seat' ? 40 :
                   20;
  const lockInPenalty = { 'none': 0, 'low': 10, 'medium': 20, 'high': 30 }[tech.migration.vendorLockIn];
  const cost = costScore - lockInPenalty;

  // Ecosystem score (0-100)
  const communityScore = { 'small': 25, 'medium': 50, 'large': 75, 'very-large': 100 }[tech.ecosystem.communitySize];
  const docsScore = { 'poor': 25, 'fair': 50, 'good': 75, 'excellent': 100 }[tech.ecosystem.documentation];
  const supportScore = tech.ecosystem.enterpriseSupport ? 100 : 50;
  const ecosystem = (communityScore + docsScore + supportScore) / 3;

  // Maturity score (0-100)
  const maturity = maturityToNumber(tech.technical.maturity);

  return {
    compliance: Math.round(compliance),
    technical: Math.round(technical),
    operational: Math.round(operational),
    cost: Math.round(cost),
    ecosystem: Math.round(ecosystem),
    maturity: Math.round(maturity),
  };
}

/**
 * Prepare heatmap data for comparison
 */
export function prepareHeatmapData(technologies: TechnologyOption[]): HeatmapData {
  return {
    technologies: technologies.map(tech => ({
      id: tech.id,
      name: tech.name,
      category: tech.category,
      scores: calculateHeatmapScores(tech),
    })),
  };
}

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Get color for a score (0-100)
 */
export function getScoreColor(score: number): string {
  if (score >= 80) return '#10B981'; // Green
  if (score >= 60) return '#F59E0B'; // Yellow
  return '#EF4444'; // Red
}

/**
 * Format large numbers for display
 */
export function formatLargeNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

/**
 * Get readable category name
 */
export function getCategoryDisplayName(category: string): string {
  return category
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
